import React, { createContext, useContext, useState, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);

    // Fetch configuration from API
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch('/api/config');
                if (response.ok) {
                    const configData = await response.json();
                    setConfig(configData);
                } else {
                    console.error('Failed to fetch config:', response.status);
                }
            } catch (error) {
                console.error('Failed to fetch config:', error);
            } finally {
                setConfigLoading(false);
            }
        };

        fetchConfig();
    }, []);

    useEffect(() => {
        // Check if user is logged in by checking for token in localStorage
        const token = localStorage.getItem('auth_token');
        if (token) {
            // In a real app, you would verify this token with your backend and fetch user details
            // including their super_admin status.
            // For now, we'll check if we have the user data in localStorage
            const userData = localStorage.getItem('user_data');

            if (userData) {
                try {
                    setUser(JSON.parse(userData));
                } catch (error) {
                    console.error('Failed to parse user data from localStorage:', error);
                    // Clear corrupted data
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user_data');
                }
            } else {
                // Default user with no super admin privileges if no user data is stored
                // If there's a token but no user data, something is inconsistent. Log out.
                console.warn("Auth token found but no user data. Logging out.");
                logout(); // Call logout to clear inconsistent state
            }
        }
        setLoading(false);
    }, []);

    const login = (userData, token) => {
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);
    };

    const logout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setUser(null);
    };

    const devModeLogin = () => {
        // Only allow in development mode
        if (!config?.isDevelopment) return;

        // Create a fake auth token and user data for development
        const devUser = {
            authenticated: true,
            isDev: true,
            is_super_admin: true, // Dev mode users are super admins by default
            name: 'Dev User',
            email: 'dev@example.com',
            id: 999
        };
        // Use the main login function for consistency
        login(devUser, 'dev-mode-token');
    };

    // Show loading until both config and auth check are complete
    if (configLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" role="status">
                    <span className="sr-only">Loading configuration...</span>
                </div>
            </div>
        );
    }

    // If config failed to load or no Google Client ID, show error
    if (!config?.googleClientId) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h1 className="text-xl font-bold text-red-600 mb-2">Configuration Error</h1>
                    <p className="text-gray-600">Google OAuth client ID not configured.</p>
                </div>
            </div>
        );
    }

    return (
        <GoogleOAuthProvider
            clientId={config.googleClientId}
        >
            <AuthContext.Provider value={{
                user,
                login,
                logout,
                loading,
                devModeLogin,
                isDevelopment: config.isDevelopment
            }}>
                {children}
            </AuthContext.Provider>
        </GoogleOAuthProvider>
    );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext; 